import React, { useCallback, useMemo } from 'react';
import { Button, Select, Text, Flex, MenuItemProps, Td, Tr } from '@televet/kibble-ui';
import { CardBrand } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { removeSelectedCardBrand, setPimsPaymentType } from '../../state/paymentSettingsSlice';
import { InfoIcon } from 'shared/components';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface ICardTypeRowProps {
  cardBrand: CardBrand;
  paymentTypes: MenuItemProps[];
  setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const CardTypeRow = ({ cardBrand, paymentTypes, setHasUnsavedChange }: ICardTypeRowProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const defaultPaymentType = useAppSelector((state) => state.paymentSettings.defaultPimsPaymentTypeId);
  const amexPaymentType = useAppSelector((state) => state.paymentSettings.amexPimsPaymentTypeId);
  const discoverPaymentType = useAppSelector((state) => state.paymentSettings.discoverPimsPaymentTypeId);
  const mastercardPaymentType = useAppSelector((state) => state.paymentSettings.mastercardPimsPaymentTypeId);
  const visaPaymentType = useAppSelector((state) => state.paymentSettings.visaPimsPaymentTypeId);
  const { primaryIntegrationName } = useIntegrationsProvider();

  const selectedPaymentType = useMemo(() => {
    let value = undefined;
    switch (cardBrand) {
      case CardBrand.Amex:
        value = amexPaymentType;
        break;
      case CardBrand.Discover:
        value = discoverPaymentType;
        break;
      case CardBrand.Mastercard:
        value = mastercardPaymentType;
        break;
      case CardBrand.Visa:
        value = visaPaymentType;
        break;
      case CardBrand.Unknown:
        value = defaultPaymentType;
        break;
      default:
        break;
    }

    return value || undefined;
  }, [amexPaymentType, cardBrand, discoverPaymentType, mastercardPaymentType, visaPaymentType, defaultPaymentType]);

  const handleSetPaymentType = useCallback(
    (selectedOption) => {
      const paymentTypeId = selectedOption.value;
      if (paymentTypeId) {
        dispatch(setPimsPaymentType({ cardBrand, paymentTypeId }));
        setHasUnsavedChange(true);
      }
    },
    [cardBrand, dispatch, setHasUnsavedChange],
  );

  const handleRemoveCardType = useCallback(
    (cardType) => {
      dispatch(removeSelectedCardBrand(cardType));
      dispatch(setPimsPaymentType({ cardBrand, paymentTypeId: null }));
      setHasUnsavedChange(true);
    },
    [dispatch, cardBrand, setHasUnsavedChange],
  );

  return (
    <Tr h="40px">
      <Td px={2} py={0} borderBottom={0}>
        <Text fontWeight="bold" size="sm">
          {cardBrand === CardBrand.Unknown ? (
            <Flex gap={1}>
              {'Other Cards'}
              <InfoIcon
                heading="Other Cards"
                text={`Many forms of payment can be accepted in Flow. If a card type is not recognized by ${primaryIntegrationName}, it
                    will show up as the payment type selected here.`}
              />
            </Flex>
          ) : (
            cardBrand
          )}
        </Text>
      </Td>
      <Td px={2} py={0} borderBottom={0}>
        <Flex>
          <Select
            size="sm"
            w="fit-content"
            formControlStyle={{ width: 'fit-content' }}
            placeholder="Select"
            options={paymentTypes}
            value={selectedPaymentType}
            variant="ghost"
            padding={0}
            _focus={{ border: 'none' }}
            listProps={{ zIndex: 10, onSelect: handleSetPaymentType }}
            useInvalidIcon={true}
            isInvalid={!selectedPaymentType}
            useInvalidText={false}
            useInvalidBorder={false}
            invalidIconColor="warning"
          />
        </Flex>
      </Td>
      <Td px={2} py={0} borderBottom={0}>
        {cardBrand !== CardBrand.Unknown && (
          <Button
            iconName="trashcan"
            size="sm"
            float="right"
            variant="ghostNeutral"
            onClick={(): void => handleRemoveCardType(cardBrand)}
          />
        )}
      </Td>
    </Tr>
  );
};

export default CardTypeRow;
